import React, { useEffect, useRef } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { FieldWrapper } from "../../../shared/ui";
import { FormInput } from "../../../shared/ui/controls/FormInput";
import { FormSelect } from "../../../shared/ui/controls/FormSelect";
import { TaskSearchSelect } from "../../../entities/task";
import { AppointmentByWorkOrderSelect } from "../../../entities/appointment";
import {
  PUSH_TARGET_CUSTOM,
  PUSH_TARGET_NONE,
  findPushTarget,
  pushTargetSelectOptions,
  type PushTargetParam,
} from "../model/pushTargets";
import type { SendPushFormValues } from "../model/sendPushFormSchema";
import { buildPushTargetUrl } from "../lib/buildPushTargetUrl";
import { describeAudience, describeTargetOption } from "../lib/describePushTarget";

/**
 * Where tapping the notification leads.
 *
 * One question with follow-ups rather than one text field: the sender picks a destination by
 * its name in the app, and only the destinations that address one specific thing ask for it.
 * Everything under the controls is derived — the sentence, the address, and who can open it —
 * so nothing here has to be remembered or guessed.
 *
 * Reads the form through context so `PushComposeForm` stays a wiring layer.
 */
export const PushTargetField: React.FC = () => {
  const { control, setValue, formState } = useFormContext<SendPushFormValues>();
  const errors = formState.errors;

  const [target, param1, param2, customUrl] = useWatch({
    control,
    name: ["target", "param1", "param2", "customUrl"],
  });

  const option = findPushTarget(target);
  const params = option?.params ?? [];
  const findParam = (kind: PushTargetParam["kind"]) => params.find((p) => p.kind === kind);
  const workOrderParam = findParam("work-order");
  const taskParam = findParam("task");
  const appointmentParam = findParam("appointment");

  /**
   * Switching the destination clears what the previous one collected. Both slots are reused
   * across targets — `param1` is an Auftragsnummer under one entry and a task id under the next
   * — so carrying a value over would silently address the wrong thing.
   */
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setValue("param1", "");
    setValue("param2", "");
    setValue("customUrl", "");
  }, [target, setValue]);

  /** A Termin belongs to one Auftrag; changing the Auftrag invalidates the pick. */
  const hasAppointmentParam = Boolean(appointmentParam);
  useEffect(() => {
    if (!hasAppointmentParam) return;
    setValue("param2", "");
  }, [param1, hasAppointmentParam, setValue]);

  const url = buildPushTargetUrl({ target, param1, param2, customUrl });
  const audience = describeAudience(option?.roles);

  /**
   * While something is still missing the preview names it rather than claiming a destination it
   * cannot reach yet. It reads as a checklist until the choice is complete, then as a statement.
   */
  const filled = { param1: param1.trim(), param2: param2.trim() };
  const missing =
    target === PUSH_TARGET_CUSTOM
      ? customUrl.trim() === ""
        ? ["Pfad"]
        : []
      : params.filter((param) => filled[param.field] === "").map((param) => param.label);

  const summary =
    missing.length > 0
      ? `Noch offen: ${missing.join(", ")}.`
      : target === PUSH_TARGET_NONE
        ? "Der Empfänger landet auf seiner Startseite."
        : option
          ? `Öffnet ${describeTargetOption(option, filled)}.`
          : "Öffnet den eingegebenen Pfad.";

  return (
    <div className="flex flex-col gap-4">
      <FieldWrapper label="Ziel" error={errors.target?.message}>
        <FormSelect name="target" control={control} options={pushTargetSelectOptions} />
      </FieldWrapper>

      {workOrderParam && (
        <FieldWrapper
          label={workOrderParam.label}
          required
          error={errors[workOrderParam.field]?.message}
        >
          <FormInput
            name={workOrderParam.field}
            control={control}
            placeholder="z. B. 12345"
            autoComplete="off"
          />
        </FieldWrapper>
      )}

      {taskParam && (
        /*
         * Keyed by target, or the two Aufgabe entries share one field instance: they render the
         * same element in the same slot, so React reconciles instead of remounting and
         * `EntitySearchSelect` keeps the search text of a task the effect above has already
         * cleared — a box reading "Heizung entlüften" over an empty value.
         */
        <FieldWrapper
          key={target}
          label={taskParam.label}
          required
          error={errors[taskParam.field]?.message}
        >
          <Controller
            control={control}
            name={taskParam.field}
            render={({ field, fieldState }) => (
              <TaskSearchSelect
                value={field.value === "" ? null : Number(field.value)}
                onChange={(id) => field.onChange(id === null ? "" : String(id))}
                error={fieldState.error?.message ?? null}
                inputRef={field.ref}
              />
            )}
          />
        </FieldWrapper>
      )}

      {appointmentParam && (
        <FieldWrapper
          label={appointmentParam.label}
          required
          error={errors[appointmentParam.field]?.message}
        >
          <Controller
            control={control}
            name={appointmentParam.field}
            render={({ field, fieldState }) => (
              <AppointmentByWorkOrderSelect
                workOrderId={param1}
                value={field.value === "" ? null : Number(field.value)}
                onChange={(id) => field.onChange(id === null ? "" : String(id))}
                error={fieldState.error?.message ?? null}
              />
            )}
          />
        </FieldWrapper>
      )}

      {target === PUSH_TARGET_CUSTOM && (
        <FieldWrapper label="Pfad" required error={errors.customUrl?.message}>
          <FormInput
            name="customUrl"
            control={control}
            placeholder="/aufgaben?upsert-task=edit&upsert-task-id=7"
            autoComplete="off"
          />
        </FieldWrapper>
      )}

      <PushTargetPreview
        summary={summary}
        url={url}
        notes={[
          audience && `Nur für ${audience} — andere Empfänger landen auf ihrer Startseite.`,
          option?.note,
        ]}
      />
    </div>
  );
};

interface PreviewProps {
  summary: string;
  url: string | undefined;
  notes: Array<string | null | undefined>;
}

/**
 * The consequence of the choice above, in the order it matters: what opens, then the address it
 * resolves to for anyone who wants to check it, then the rules that come with it.
 */
const PushTargetPreview: React.FC<PreviewProps> = ({ summary, url, notes }) => {
  const visibleNotes = notes.filter((note): note is string => Boolean(note));

  return (
    <div className="flex flex-col gap-1.5 rounded-[16px] bg-[#F2F6FB] px-4 py-3 font-['PT_Sans']">
      <p className="text-[14px] leading-[19px] text-[#004494]">{summary}</p>

      {url && (
        // Genuinely an address, not decoration — this is the string that goes over the wire.
        <code className="break-all font-mono text-[12px] leading-[16px] text-[#5B6B80]">{url}</code>
      )}

      {visibleNotes.map((note) => (
        <p key={note} className="text-[13px] leading-[18px] text-[#4B5563]">
          {note}
        </p>
      ))}
    </div>
  );
};
